import { useState } from 'react';
import { useApp } from '@/context/AppContext';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { 
  Upload, Play, Download, Trash2, Film, Clock, 
  Sparkles, ChevronRight, Plus, MessageSquare,
  Type, ImageIcon, Volume2, Edit3
} from 'lucide-react';
import { toast } from 'sonner';
import { VideoEditorPage } from '@/components/VideoEditor';

// Default folders for talking videos
const DEFAULT_FOLDERS = Array.from({ length: 10 }, (_, i) => ({
  id: `talking-${i + 1}`,
  name: `Reel ${i + 1}`,
  clips: [],
  generatedVideo: null
}));

// Subtitle style options
const SUBTITLE_STYLES = [
  { id: 'minimal', name: 'Minimal', preview: 'Clean white text' },
  { id: 'bold', name: 'Bold', preview: 'Large yellow highlights' },
  { id: 'karaoke', name: 'Karaoke', preview: 'Word-by-word highlight' },
  { id: 'boxed', name: 'Boxed', preview: 'Background boxes' },
];

export default function TalkingVideosPage() {
  const { activeBrand, trialActive } = useApp();
  const [folders, setFolders] = useState(DEFAULT_FOLDERS);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [completedProjects, setCompletedProjects] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [progress, setProgress] = useState(0);
  const [editingProject, setEditingProject] = useState(null); // Project being edited
  
  // Generation options
  const [subtitleStyle, setSubtitleStyle] = useState('bold');
  const [enableBroll, setEnableBroll] = useState(false);
  const [showOptions, setShowOptions] = useState(false);
  const [folderToGenerate, setFolderToGenerate] = useState(null);

  // Handle clip upload
  const handleClipUpload = (folderId, files) => {
    if (!activeBrand) {
      toast.error('Please select a brand first');
      return;
    }

    const newClips = Array.from(files).map((file, i) => ({
      id: `${Date.now()}-${i}`,
      name: file.name,
      file: file,
      thumbnail: null,
      duration: '0:00'
    }));

    setFolders(prev => prev.map(f => 
      f.id === folderId 
        ? { ...f, clips: [...f.clips, ...newClips] }
        : f
    ));
    
    toast.success(`${newClips.length} clip(s) added`);
  };

  // Open generation options
  const openGenerateOptions = (folder) => {
    if (folder.clips.length === 0) {
      toast.error('Add clips to this folder first');
      return;
    }
    setFolderToGenerate(folder);
    setShowOptions(true);
  };

  // Generate talking video
  const handleGenerate = async () => {
    if (!folderToGenerate) return;
    
    setShowOptions(false);
    setGenerating(true);
    setProgress(0);

    // Simulate progress
    const interval = setInterval(() => {
      setProgress(p => {
        if (p >= 95) {
          clearInterval(interval);
          return p;
        }
        return p + Math.random() * 10;
      });
    }, 500);

    // Simulate generation
    setTimeout(() => {
      clearInterval(interval);
      setProgress(100);
      
      const newProject = {
        id: Date.now().toString(),
        name: `${folderToGenerate.name} - Talking Video`,
        thumbnail: null,
        duration: '1:30',
        createdAt: new Date().toISOString(),
        fromFolder: folderToGenerate.name,
        subtitleStyle,
        hasBroll: enableBroll
      };
      
      setCompletedProjects(prev => [newProject, ...prev]);
      setGenerating(false);
      setProgress(0);
      setFolderToGenerate(null);
      toast.success('Talking video generated! Check Completed Projects.');
    }, 4000);
  };

  // Remove clip
  const handleRemoveClip = (folderId, clipId) => {
    setFolders(prev => prev.map(f => 
      f.id === folderId 
        ? { ...f, clips: f.clips.filter(c => c.id !== clipId) }
        : f
    ));
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold mb-2" style={{ color: 'hsl(var(--foreground))' }}>
          Talking Videos
        </h1>
        <p className="text-sm" style={{ color: 'hsl(var(--foreground-muted))' }}>
          Edit talking-head content with automatic hooks, silence removal, and subtitles
        </p>
        {activeBrand && (
          <p className="text-xs mt-2" style={{ color: 'hsl(var(--primary))' }}>
            Brand: {activeBrand.name} • {activeBrand.content_type || 'General'}
          </p>
        )}
      </div>

      {/* Generation Progress */}
      {generating && (
        <div className="mb-6 p-4 rounded-xl" style={{ background: 'hsl(var(--surface))', border: '1px solid hsl(var(--border))' }}>
          <div className="flex items-center gap-3 mb-3">
            <div className="animate-spin">
              <Sparkles size={20} style={{ color: 'hsl(var(--primary))' }} />
            </div>
            <span className="text-sm font-medium" style={{ color: 'hsl(var(--foreground))' }}>
              Processing talking video...
            </span>
          </div>
          <div className="h-2 rounded-full overflow-hidden" style={{ background: 'hsl(var(--surface-elevated))' }}>
            <div 
              className="h-full transition-all duration-300 rounded-full"
              style={{ width: `${progress}%`, background: 'hsl(var(--primary))' }}
            />
          </div>
          <div className="mt-3 text-xs space-y-1" style={{ color: 'hsl(var(--foreground-subtle))' }}>
            <p>• Detecting viral hook moment...</p>
            <p>• Removing silence...</p>
            <p>• Adding subtitles ({SUBTITLE_STYLES.find(s => s.id === subtitleStyle)?.name})...</p>
            {enableBroll && <p>• Inserting B-Roll...</p>}
          </div>
          <p className="text-xs mt-3" style={{ color: 'hsl(var(--warning))' }}>
            Please keep this tab open while the video is generating.
          </p>
        </div>
      )}

      {/* Features info */}
      <div className="mb-8 grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { icon: Sparkles, label: 'Hook Detection', desc: 'Find viral moments' },
          { icon: Volume2, label: 'Silence Removal', desc: 'Clean audio' },
          { icon: Type, label: 'Auto Subtitles', desc: 'Multiple styles' },
          { icon: ImageIcon, label: 'B-Roll Insert', desc: 'From your library' },
        ].map(feature => (
          <div key={feature.label} className="p-4 rounded-xl" style={{ background: 'hsl(var(--surface))', border: '1px solid hsl(var(--border))' }}>
            <feature.icon size={24} className="mb-2" style={{ color: 'hsl(var(--primary))' }} />
            <p className="text-sm font-medium" style={{ color: 'hsl(var(--foreground))' }}>{feature.label}</p>
            <p className="text-xs" style={{ color: 'hsl(var(--foreground-subtle))' }}>{feature.desc}</p>
          </div>
        ))}
      </div>

      {/* Folders Grid */}
      <div className="mb-8">
        <h2 className="text-lg font-semibold mb-4" style={{ color: 'hsl(var(--foreground))' }}>
          Video Folders
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
          {folders.map(folder => (
            <div 
              key={folder.id}
              className="rounded-xl overflow-hidden transition-transform hover:scale-[1.02]"
              style={{ background: 'hsl(var(--surface))', border: '1px solid hsl(var(--border))' }}
            >
              <div 
                className="p-3 cursor-pointer"
                onClick={() => setSelectedFolder(selectedFolder?.id === folder.id ? null : folder)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <MessageSquare size={16} style={{ color: 'hsl(var(--primary))' }} />
                    <span className="text-sm font-medium" style={{ color: 'hsl(var(--foreground))' }}>
                      {folder.name}
                    </span>
                  </div>
                  <ChevronRight 
                    size={16} 
                    className={`transition-transform ${selectedFolder?.id === folder.id ? 'rotate-90' : ''}`}
                    style={{ color: 'hsl(var(--foreground-subtle))' }}
                  />
                </div>
                <p className="text-xs mt-1" style={{ color: 'hsl(var(--foreground-subtle))' }}>
                  {folder.clips.length} clips
                </p>
              </div>

              <label 
                className="block p-3 mx-3 mb-3 rounded-lg cursor-pointer text-center transition-colors hover:bg-surface-hover"
                style={{ border: '1px dashed hsl(var(--border))' }}
              >
                <input 
                  type="file" 
                  accept="video/*" 
                  multiple
                  className="hidden"
                  onChange={(e) => handleClipUpload(folder.id, e.target.files)}
                />
                <Plus size={16} className="mx-auto" style={{ color: 'hsl(var(--foreground-subtle))' }} />
                <span className="text-xs" style={{ color: 'hsl(var(--foreground-subtle))' }}>
                  Add clips
                </span>
              </label>

              {folder.clips.length > 0 && (
                <div className="px-3 pb-3">
                  <Button 
                    size="sm" 
                    className="w-full h-8 text-xs"
                    onClick={() => openGenerateOptions(folder)}
                    disabled={generating}
                  >
                    <Sparkles size={14} className="mr-1" />
                    Generate
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Selected Folder Detail */}
      {selectedFolder && (
        <div className="mb-8 p-6 rounded-xl" style={{ background: 'hsl(var(--surface))', border: '1px solid hsl(var(--border))' }}>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold" style={{ color: 'hsl(var(--foreground))' }}>
              {selectedFolder.name} - Clips
            </h3>
            <Button variant="ghost" size="sm" onClick={() => setSelectedFolder(null)}>
              Close
            </Button>
          </div>

          {selectedFolder.clips.length === 0 ? (
            <p className="text-sm text-center py-8" style={{ color: 'hsl(var(--foreground-muted))' }}>
              No clips yet. Upload talking-head videos to this folder.
            </p>
          ) : (
            <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
              {selectedFolder.clips.map(clip => (
                <div key={clip.id} className="group relative">
                  <div 
                    className="aspect-[9/16] rounded-lg overflow-hidden"
                    style={{ background: 'hsl(var(--surface-elevated))' }}
                  >
                    <div className="w-full h-full flex items-center justify-center">
                      <MessageSquare size={24} style={{ color: 'hsl(var(--foreground-subtle))' }} />
                    </div>
                    <button
                      onClick={() => handleRemoveClip(selectedFolder.id, clip.id)}
                      className="absolute top-1 right-1 p-1 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                      style={{ background: 'hsl(var(--destructive))', color: '#fff' }}
                    >
                      <Trash2 size={12} />
                    </button>
                  </div>
                  <p className="text-xs mt-1 truncate" style={{ color: 'hsl(var(--foreground-subtle))' }}>
                    {clip.name}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Completed Projects */}
      <div>
        <h2 className="text-lg font-semibold mb-4" style={{ color: 'hsl(var(--foreground))' }}>
          Completed Projects
        </h2>

        {completedProjects.length === 0 ? (
          <div className="text-center py-12 rounded-xl" style={{ background: 'hsl(var(--surface))', border: '1px solid hsl(var(--border))' }}>
            <MessageSquare size={40} className="mx-auto mb-3" style={{ color: 'hsl(var(--foreground-subtle))' }} />
            <p className="text-sm" style={{ color: 'hsl(var(--foreground-muted))' }}>
              No completed videos yet
            </p>
            <p className="text-xs mt-1" style={{ color: 'hsl(var(--foreground-subtle))' }}>
              Add talking-head clips and generate your first video
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {completedProjects.map(project => (
              <div 
                key={project.id}
                className="group rounded-xl overflow-hidden"
                style={{ background: 'hsl(var(--surface))', border: '1px solid hsl(var(--border))' }}
              >
                <div className="aspect-[9/16] relative" style={{ background: 'hsl(var(--surface-elevated))' }}>
                  <div className="w-full h-full flex items-center justify-center">
                    <MessageSquare size={32} style={{ color: 'hsl(var(--foreground-subtle))' }} />
                  </div>
                  <div className="absolute bottom-2 right-2 px-2 py-0.5 rounded text-xs"
                    style={{ background: 'rgba(0,0,0,0.7)', color: '#fff' }}>
                    {project.duration}
                  </div>
                  {trialActive && (
                    <div className="absolute top-2 left-2 px-2 py-0.5 rounded text-xs"
                      style={{ background: 'hsl(var(--warning))', color: '#000' }}>
                      Watermark
                    </div>
                  )}
                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                    <Button size="sm" variant="secondary">
                      <Play size={14} className="mr-1" /> Preview
                    </Button>
                    <Button size="sm" variant="secondary" onClick={() => setEditingProject(project)}>
                      <Edit3 size={14} className="mr-1" /> Edit
                    </Button>
                    <Button size="sm">
                      <Download size={14} className="mr-1" /> Download
                    </Button>
                  </div>
                </div>
                <div className="p-3">
                  <p className="text-sm font-medium truncate" style={{ color: 'hsl(var(--foreground))' }}>
                    {project.name}
                  </p>
                  <p className="text-xs mt-1" style={{ color: 'hsl(var(--foreground-subtle))' }}>
                    {project.subtitleStyle} subtitles {project.hasBroll && '• B-Roll'}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Generation Options Modal */}
      {showOptions && folderToGenerate && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: 'rgba(0,0,0,0.8)' }}
          onClick={() => setShowOptions(false)}
        >
          <div 
            className="w-full max-w-md rounded-xl p-6"
            style={{ background: 'hsl(var(--background))' }}
            onClick={e => e.stopPropagation()}
          >
            <h2 className="text-xl font-bold mb-4" style={{ color: 'hsl(var(--foreground))' }}>
              Generation Options
            </h2>
            <p className="text-sm mb-6" style={{ color: 'hsl(var(--foreground-muted))' }}>
              Configure how to process "{folderToGenerate.name}"
            </p>

            {/* Subtitle Style */}
            <div className="mb-6">
              <label className="text-sm font-medium mb-3 block" style={{ color: 'hsl(var(--foreground))' }}>
                Subtitle Style
              </label>
              <div className="grid grid-cols-2 gap-2">
                {SUBTITLE_STYLES.map(style => (
                  <button
                    key={style.id}
                    onClick={() => setSubtitleStyle(style.id)}
                    className={`p-3 rounded-lg text-left transition-colors ${subtitleStyle === style.id ? 'ring-2 ring-primary' : ''}`}
                    style={{ 
                      background: subtitleStyle === style.id ? 'hsl(var(--primary) / 0.1)' : 'hsl(var(--surface))',
                      border: '1px solid hsl(var(--border))'
                    }}
                  >
                    <p className="text-sm font-medium" style={{ color: 'hsl(var(--foreground))' }}>{style.name}</p>
                    <p className="text-xs" style={{ color: 'hsl(var(--foreground-subtle))' }}>{style.preview}</p>
                  </button>
                ))}
              </div>
            </div>

            {/* B-Roll Toggle */}
            <div className="mb-6 flex items-center justify-between p-4 rounded-lg" style={{ background: 'hsl(var(--surface))', border: '1px solid hsl(var(--border))' }}>
              <div>
                <p className="text-sm font-medium" style={{ color: 'hsl(var(--foreground))' }}>
                  Insert B-Roll
                </p>
                <p className="text-xs" style={{ color: 'hsl(var(--foreground-subtle))' }}>
                  Automatically add B-Roll from your library
                </p>
              </div>
              <Switch checked={enableBroll} onCheckedChange={setEnableBroll} />
            </div>

            {/* Actions */}
            <div className="flex gap-3">
              <Button variant="ghost" className="flex-1" onClick={() => setShowOptions(false)}>
                Cancel
              </Button>
              <Button className="flex-1" onClick={handleGenerate}>
                <Sparkles size={16} className="mr-2" />
                Generate
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Video Editor */}
      {editingProject && (
        <VideoEditorPage
          video={{
            id: editingProject.id,
            title: editingProject.name,
            name: editingProject.name,
            url: null,
            durationSeconds: 90,
          }}
          moduleType="talking"
          onSave={(updatedVideo) => {
            setCompletedProjects(prev => prev.map(p =>
              p.id === editingProject.id ? { ...p, name: updatedVideo.settings.title, settings: updatedVideo.settings } : p
            ));
            setEditingProject(null);
          }}
          onClose={() => setEditingProject(null)}
          onExport={(video) => {
            toast.success(`Exporting "${video.settings.title}"...`);
            setEditingProject(null);
          }}
        />
      )}
    </div>
  );
}
